import { useEffect, useState } from "react";
import api, { fmt } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendChart, MetricBarChart, DonutChart } from "@/components/Charts";

const RANGES = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "7 Days" },
  { value: "14d", label: "14 Days" },
  { value: "30d", label: "30 Days" },
  { value: "lifetime", label: "Campaign Lifetime" },
];

export default function Analytics() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [range, setRange] = useState("lifetime");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/client/campaigns").then((r) => {
      setCampaigns(r.data);
      if (r.data[0]) setCampaignId(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!campaignId) return;
    setData(null);
    api.get(`/client/analytics/campaign/${campaignId}?range=${range}`).then((r) => setData(r.data)).catch(() => {});
  }, [campaignId, range]);

  const t = data?.totals;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="section-label">Analytics</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Campaign Analytics</h2>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Select value={campaignId} onValueChange={setCampaignId}>
            <SelectTrigger className="w-64 bg-card border-white/10" data-testid="analytics-campaign-select"><SelectValue placeholder="Select campaign" /></SelectTrigger>
            <SelectContent className="bg-popover border-white/10">
              {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-44 bg-card border-white/10" data-testid="analytics-range-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-white/10">
              {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!data ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48 bg-white/5" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[["Impressions", fmt(t.impressions)], ["Reach", fmt(t.reach)], ["Engagements", fmt(t.engagements)],
              ["Engagement Rate", `${t.engagement_rate}%`], ["CTR", `${t.ctr}%`]].map(([label, v]) => (
              <div key={label} className="card-surface p-4" data-testid={`analytics-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="font-display text-xl font-bold text-white">{v}</p>
                <p className="text-[10px] tracking-widest text-slate-500 uppercase mt-1">{label}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="card-surface p-5">
              <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">PERFORMANCE TREND</h3>
              {data.series.length ? (
                <TrendChart data={data.series} keys={[
                  { key: "impressions", color: "#3b82f6", name: "Impressions" },
                  { key: "reach", color: "#6366f1", name: "Reach" },
                  { key: "engagements", color: "#06b6d4", name: "Engagements" },
                ]} />
              ) : <p className="text-sm text-slate-500 py-16 text-center">No activity in this range yet.</p>}
            </div>
            <div className="card-surface p-5">
              <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">ENGAGEMENT SHARE BY LOCATION</h3>
              <DonutChart data={data.locations.map((l) => ({ name: l.location_name, value: l.engagements }))} />
              <div className="flex flex-wrap gap-3 justify-center mt-2">
                {data.locations.map((l, i) => (
                  <span key={l.geofence_id} className="text-xs text-slate-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: ["#3b82f6", "#06b6d4", "#22d3ee"][i % 3] }} />
                    {l.location_name}
                  </span>
                ))}
              </div>
            </div>
            <div className="card-surface p-5">
              <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">CAMPAIGN COST PER LOCATION</h3>
              <MetricBarChart data={data.locations.map((l) => ({ name: l.location_name.replace("Johannesburg", "JHB"), cost: l.cost }))} barKey="cost" color="#0ea5e9" />
              <p className="text-xs text-slate-500 mt-3 text-center">
                Total media investment <span className="text-white font-semibold">R{fmt(data.locations.reduce((s, l) => s + (l.cost || 0), 0))}</span>
              </p>
            </div>
            <div className="card-surface p-5">
              <h3 className="font-display font-semibold text-sm tracking-wide text-white mb-4">CLICKS BY LOCATION</h3>
              <MetricBarChart data={data.locations.map((l) => ({ name: l.location_name.split(" ")[0], clicks: l.clicks }))} barKey="clicks" color="#3b82f6" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
